import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Loader2, Save, ShieldAlert, Wind, Box, Sigma, RotateCcw, FileText, History, Trash2, Archive } from "lucide-react";
import { toast } from "sonner";

const num = (v) => (v === "" || v == null ? "" : Number(v));

function NumField({ label, value, onChange, step = "any", suffix, testid, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-xs">{label}</Label>
      <div className="flex items-center gap-1.5">
        <Input type="number" step={step} value={value ?? ""} data-testid={testid}
          onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))} className="h-8" />
        {suffix && <span className="text-[11px] text-muted-foreground">{suffix}</span>}
      </div>
    </div>
  );
}

export default function ParametriDimensionamento() {
  const [cfg, setCfg] = useState(null);
  const [canEdit, setCanEdit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [versions, setVersions] = useState([]);
  const [verLabel, setVerLabel] = useState("");
  const [verBusy, setVerBusy] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/prsg-zavorre/config").then((r) => {
      setCfg(r.data.config); setCanEdit(!!r.data.can_edit);
    }).catch(() => toast.error("Errore nel caricamento parametri")).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const loadVersions = () => {
    api.get("/prsg-zavorre/config/versions").then((r) => setVersions(r.data.versions || [])).catch(() => {});
  };
  useEffect(loadVersions, []);

  const upd = (mutator) => setCfg((c) => { const n = structuredClone(c); mutator(n); return n; });

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/prsg-zavorre/config", { config: cfg });
      setCfg(r.data.config);
      toast.success("Parametri di dimensionamento salvati");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel salvataggio");
    } finally { setSaving(false); }
  };

  const downloadRelazione = async () => {
    setPdfLoading(true);
    try {
      const r = await api.post("/prsg-zavorre/relazione-pdf", { config: cfg }, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = "relazione-tecnica-motore-zavorre.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Errore nel download della relazione");
    } finally { setPdfLoading(false); }
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
  };

  const saveVersion = async () => {
    setVerBusy(true);
    try {
      await api.post("/prsg-zavorre/config/versions", { config: cfg, label: verLabel });
      setVerLabel("");
      toast.success("Versione del motore salvata");
      loadVersions();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel salvataggio versione");
    } finally { setVerBusy(false); }
  };

  const restoreVersion = async (v) => {
    if (!window.confirm(`Ripristinare la versione del ${fmtDate(v.created_at)}${v.label ? ` — "${v.label}"` : ""}?\nI parametri correnti verranno sostituiti.`)) return;
    setVerBusy(true);
    try {
      const r = await api.post(`/prsg-zavorre/config/versions/${v.id}/restore`);
      setCfg(r.data.config);
      toast.success("Versione ripristinata");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel ripristino");
    } finally { setVerBusy(false); }
  };

  const deleteVersion = async (v) => {
    if (!window.confirm(`Eliminare definitivamente questa versione salvata?`)) return;
    setVerBusy(true);
    try {
      await api.delete(`/prsg-zavorre/config/versions/${v.id}`);
      toast.success("Versione eliminata");
      loadVersions();
    } catch (e) {
      toast.error("Errore nell'eliminazione");
    } finally { setVerBusy(false); }
  };

  if (loading || !cfg) {
    return <div className="py-20 text-center text-muted-foreground"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>;
  }

  const torri = cfg.torri, aero = cfg.aero, ntc = cfg.ntc;

  return (
    <div className="space-y-6 pb-20" data-testid="parametri-dimensionamento-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight">Parametri di dimensionamento</h1>
          <p className="text-sm text-muted-foreground">Motore di pre-dimensionamento zavorre PRSG su tetto piano. Tre livelli separati: normativa NTC, dati fisici TORRI, coefficienti aerodinamici.</p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <Badge variant="secondary" data-testid="param-version"><Wind className="mr-1 h-3 w-3" /> {cfg.version}</Badge>
            <span className="text-[11px] text-muted-foreground">{cfg.method_label}</span>
            {aero.validated
              ? <Badge className="bg-emerald-600 text-white">Coefficienti aero VALIDATI</Badge>
              : <Badge variant="destructive" data-testid="param-aero-davalidare"><ShieldAlert className="mr-1 h-3 w-3" /> Aero DA VALIDARE</Badge>}
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={downloadRelazione} disabled={pdfLoading} data-testid="param-relazione-pdf">
            {pdfLoading ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <FileText className="mr-1.5 h-4 w-4" />} Scarica relazione PDF
          </Button>
          {canEdit && (
            <Button onClick={save} disabled={saving} data-testid="param-save">{saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />} Salva</Button>
          )}
        </div>
      </div>

      {!canEdit && <div className="rounded-md border border-amber-400/40 bg-amber-400/10 p-3 text-xs text-amber-600 dark:text-amber-300">Sola lettura: solo Admin e Back Office possono modificare i parametri.</div>}

      {/* VERSIONI DEL MOTORE — snapshot manuali con ripristino */}
      <Card className="p-5" data-testid="param-versioni-card">
        <div className="mb-1 flex items-center gap-2"><History className="h-4 w-4 text-primary" /><h2 className="font-heading text-lg font-bold">Versioni del motore di calcolo</h2></div>
        <p className="mb-3 text-xs text-muted-foreground">Salva manualmente una copia dei parametri correnti con data. In caso di errori puoi ripristinare una versione precedente con un clic.</p>
        {canEdit && (
          <div className="mb-4 flex flex-wrap items-end gap-2">
            <div className="flex-1 min-w-[220px]">
              <Label className="text-xs">Nome versione (facoltativo)</Label>
              <Input value={verLabel} onChange={(e) => setVerLabel(e.target.value)} placeholder="es. Calibrazione parapetti pre-strutturista"
                className="h-8" data-testid="param-versione-label" />
            </div>
            <Button onClick={saveVersion} disabled={verBusy} data-testid="param-versione-save">
              {verBusy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Archive className="mr-1.5 h-4 w-4" />} Salva versione corrente
            </Button>
          </div>
        )}
        {versions.length === 0 ? (
          <div className="rounded-md border border-dashed border-input p-4 text-center text-xs text-muted-foreground" data-testid="param-versioni-empty">
            Nessuna versione salvata. Salva la configurazione corrente per poterla ripristinare in seguito.
          </div>
        ) : (
          <div className="space-y-2" data-testid="param-versioni-list">
            {versions.map((v) => (
              <div key={v.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-input p-3" data-testid={`param-versione-${v.id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold">{v.label || "Senza nome"}</span>
                    {v.version && <Badge variant="secondary" className="text-[10px]">{v.version}</Badge>}
                  </div>
                  <div className="text-[11px] text-muted-foreground">{fmtDate(v.created_at)}{v.created_by ? ` · ${v.created_by}` : ""}</div>
                </div>
                {canEdit && (
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => restoreVersion(v)} disabled={verBusy} data-testid={`param-versione-restore-${v.id}`}>
                      <RotateCcw className="mr-1.5 h-3.5 w-3.5" /> Ripristina
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => deleteVersion(v)} disabled={verBusy}
                      className="text-destructive hover:text-destructive" data-testid={`param-versione-delete-${v.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* LIVELLO 2 - TORRI fisico */}
      <Card className="p-5" data-testid="param-torri-card">
        <div className="mb-3 flex items-center gap-2"><Box className="h-4 w-4 text-primary" /><h2 className="font-heading text-lg font-bold">Dati fisici TORRI (modificabili)</h2></div>

        <div className="mb-4">
          <div className="mb-1 font-mono text-[11px] font-bold uppercase tracking-widest text-primary">Anagrafica modelli PRSG</div>
          {Object.entries(torri.prsg_models || {}).map(([id, m]) => (
            <div key={id} className="mb-3 rounded-lg border border-input p-3" data-testid={`param-prsg-model-${id}`}>
              <div className="mb-2 flex items-center gap-2">
                <span className="text-xs font-bold">{m.label || id}</span>
                <Badge variant="outline" className="text-[10px]">{m.center_of_mass_status || "—"}</Badge>
              </div>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                <NumField label="Base a terra" suffix="mm" value={m.ground_base_mm} testid={`param-prsg-${id}-base`} onChange={(v) => upd((c) => c.torri.prsg_models[id].ground_base_mm = v)} />
                <NumField label="Altezza post." suffix="mm" value={m.rear_height_mm} testid={`param-prsg-${id}-h`} onChange={(v) => upd((c) => c.torri.prsg_models[id].rear_height_mm = v)} />
                <NumField label="Inclinazione" suffix="°" value={m.tilt_deg} testid={`param-prsg-${id}-tilt`} onChange={(v) => upd((c) => c.torri.prsg_models[id].tilt_deg = v)} />
                <NumField label="Peso" suffix="kg" value={m.mass_kg} testid={`param-prsg-${id}-mass`} onChange={(v) => upd((c) => c.torri.prsg_models[id].mass_kg = v)} />
                <NumField label="CG X" suffix="mm" value={m.center_of_mass_x_mm} testid={`param-prsg-${id}-cgx`} onChange={(v) => upd((c) => c.torri.prsg_models[id].center_of_mass_x_mm = v)} />
                <NumField label="CG Z" suffix="mm" value={m.center_of_mass_z_mm} testid={`param-prsg-${id}-cgz`} onChange={(v) => upd((c) => c.torri.prsg_models[id].center_of_mass_z_mm = v)} />
                <NumField label="Asse rot. ant." suffix="mm" value={m.front_rotation_axis_x_mm} testid={`param-prsg-${id}-axf`} onChange={(v) => upd((c) => c.torri.prsg_models[id].front_rotation_axis_x_mm = v)} />
                <NumField label="Asse rot. post." suffix="mm" value={m.rear_rotation_axis_x_mm} testid={`param-prsg-${id}-axr`} onChange={(v) => upd((c) => c.torri.prsg_models[id].rear_rotation_axis_x_mm = v)} />
              </div>
            </div>
          ))}
          <p className="mt-1 text-[11px] text-muted-foreground">Baricentri e assi di rotazione stimati da disegno tecnico (ESTIMATED_NOT_CAD_VALIDATED): sostituibili con i dati CAD senza modificare le formule.</p>
        </div>

        <div className="mb-4">
          <div className="mb-1 font-mono text-[11px] font-bold uppercase tracking-widest text-primary">Attrito a due interfacce per superficie (μ zavorra CLS / μ PRSG zincata)</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[11px] text-muted-foreground"><tr><th className="p-1 text-left">Superficie copertura</th><th className="p-1">μ Zavorra CLS</th><th className="p-1">μ PRSG zincata</th><th className="p-1 text-left">Stato consigliato</th><th className="p-1">Maschera</th></tr></thead>
              <tbody>
                {Object.entries(torri.friction_two_interface_by_surface || {}).map(([s, e]) => (
                  <tr key={s} className="border-t border-input">
                    <td className="p-1">{e.label || s.replace(/_/g, " ")}</td>
                    <td className="p-1"><Input type="number" step="0.01" value={e.mu_ballast} className="h-7 w-20" data-testid={`param-mu-${s}-ballast`} onChange={(ev) => upd((c) => c.torri.friction_two_interface_by_surface[s].mu_ballast = Number(ev.target.value))} /></td>
                    <td className="p-1"><Input type="number" step="0.01" value={e.mu_prsg} className="h-7 w-20" data-testid={`param-mu-${s}-prsg`} onChange={(ev) => upd((c) => c.torri.friction_two_interface_by_surface[s].mu_prsg = Number(ev.target.value))} /></td>
                    <td className="p-1"><Badge variant={(e.status === "EXPERIMENTAL_TORRI" || e.status === "VALIDATED") ? "secondary" : (e.status === "FALLBACK" ? "destructive" : "outline")} className="text-[10px]">{e.status}</Badge></td>
                    <td className="p-1 text-center"><input type="checkbox" className="h-4 w-4 accent-primary cursor-pointer" checked={e.show_in_form !== false} data-testid={`param-show-${s}`} onChange={(ev) => upd((c) => c.torri.friction_two_interface_by_surface[s].show_in_form = ev.target.checked)} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-2 space-y-1 rounded-md border border-input bg-muted/20 p-2.5 text-[11px] text-muted-foreground" data-testid="param-attrito-legenda">
            <div className="font-semibold text-foreground">Legenda</div>
            <div><b>μ Zavorra CLS</b>: attrito tra la zavorra in <b>calcestruzzo</b> e la copertura. <b>μ PRSG zincata</b>: attrito tra la <b>staffa presagomata zincata</b> e la copertura. Nella verifica allo scorrimento si usa <b>un solo</b> μ (quello dell'elemento a contatto col tetto: zavorra <i>oppure</i> staffa).</div>
            <div className="pt-0.5"><Badge variant="secondary" className="mr-1 text-[10px]">EXPERIMENTAL_TORRI</Badge> valore <b>misurato da TORRI</b> (prova al dinamometro) — affidabile.</div>
            <div><Badge variant="outline" className="mr-1 text-[10px]">EXTERNAL_CONSERVATIVE</Badge> valore da fonte <b>esterna</b> (schede/letteratura) assunto in modo <b>cautelativo</b>.</div>
            <div><Badge variant="outline" className="mr-1 text-[10px]">CONSERVATIVE_PRESET</Badge> preset <b>prudenziale</b> di pre-dimensionamento, da validare con prova.</div>
            <div><Badge variant="destructive" className="mr-1 text-[10px]">FALLBACK</Badge> valore <b>minimo di sicurezza</b> per superficie sconosciuta/non classificata.</div>
            <div className="pt-0.5 italic">Solo la guaina bituminosa è validata sperimentalmente da TORRI (0,58 / 0,40). Per le altre superfici, in assenza di prova dedicata, il pre-dimensionamento resta cautelativo: valida con prova separata per ciascuna interfaccia prima dell'uso definitivo.</div>
          </div>
        </div>

        <div className="mb-4">
          <div className="mb-1 font-mono text-[11px] font-bold uppercase tracking-widest text-primary">Anagrafica zavorra (blocchi disponibili)</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[11px] text-muted-foreground"><tr><th className="p-1 text-left">Codice</th><th className="p-1 text-left">Etichetta</th><th className="p-1">Peso (kg)</th><th className="p-1">L (mm)</th><th className="p-1">W (mm)</th><th className="p-1">H (mm)</th></tr></thead>
              <tbody>
                {(torri.ballast_options || []).map((b, i) => (
                  <tr key={b.code || i} className="border-t border-input">
                    <td className="p-1 font-mono text-[11px]">{b.code}</td>
                    <td className="p-1 text-[11px]">{b.label}</td>
                    <td className="p-1"><Input type="number" step="0.1" value={b.weight_kg} className="h-7 w-20" data-testid={`param-ballast-${i}-w`} onChange={(ev) => upd((c) => c.torri.ballast_options[i].weight_kg = Number(ev.target.value))} /></td>
                    <td className="p-1"><Input type="number" value={b.length_mm} className="h-7 w-20" data-testid={`param-ballast-${i}-l`} onChange={(ev) => upd((c) => c.torri.ballast_options[i].length_mm = Number(ev.target.value))} /></td>
                    <td className="p-1"><Input type="number" value={b.width_mm} className="h-7 w-20" data-testid={`param-ballast-${i}-wd`} onChange={(ev) => upd((c) => c.torri.ballast_options[i].width_mm = Number(ev.target.value))} /></td>
                    <td className="p-1"><Input type="number" value={b.height_mm} className="h-7 w-20" data-testid={`param-ballast-${i}-h`} onChange={(ev) => upd((c) => c.torri.ballast_options[i].height_mm = Number(ev.target.value))} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div>
          <div className="mb-1 font-mono text-[11px] font-bold uppercase tracking-widest text-primary">Coefficienti di sicurezza</div>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <NumField label="γ sollevamento" value={torri.safety_factors.uplift} testid="param-sf-uplift" onChange={(v) => upd((c) => c.torri.safety_factors.uplift = v)} />
            <NumField label="γ scorrimento" value={torri.safety_factors.sliding} testid="param-sf-sliding" onChange={(v) => upd((c) => c.torri.safety_factors.sliding = v)} />
            <NumField label="γ ribaltamento" value={torri.safety_factors.overturning} testid="param-sf-over" onChange={(v) => upd((c) => c.torri.safety_factors.overturning = v)} />
            <NumField label="γ pesi favorevoli" value={torri.safety_factors.gamma_favorable} testid="param-sf-fav" onChange={(v) => upd((c) => c.torri.safety_factors.gamma_favorable = v)} />
          </div>
          <p className="mt-1.5 text-[11px] text-muted-foreground">Il peso del modulo è acquisito automaticamente dal pannello selezionato (scheda a listino) e non è più un parametro editabile.</p>
        </div>
      </Card>

      {/* LIVELLO 3 - AERO */}
      <Card className="p-5" data-testid="param-aero-card">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2"><Sigma className="h-4 w-4 text-primary" /><h2 className="font-heading text-lg font-bold">Coefficienti aerodinamici PRSG</h2></div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Coefficienti calibrati</span>
            <Switch checked={!!aero.validated} onCheckedChange={(v) => upd((c) => c.aero.validated = v)} data-testid="param-aero-validated" />
          </div>
        </div>
        {!aero.validated && (
          <div className="mb-3 flex items-start gap-1.5 rounded-md border border-destructive/40 bg-destructive/10 p-2 text-[11px] text-destructive" data-testid="param-aero-warn">
            <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {aero.note} Finché non validati, ogni risultato riporta lo stato «PRE-DIMENSIONAMENTO – coefficienti aerodinamici da validare».
          </div>
        )}
        <div className="mb-3 flex flex-wrap gap-4">
          <div>
            <Label className="text-xs">Dimensione caratteristica L_ref</Label>
            <select value={aero.L_ref_source} data-testid="param-aero-lref" className="ml-2 h-8 rounded border border-input bg-background px-2 text-sm"
              onChange={(e) => upd((c) => c.aero.L_ref_source = e.target.value)}>
              <option value="prsg_base">Base PRSG (B)</option>
              <option value="prsg_height">Altezza PRSG (H)</option>
            </select>
          </div>
          <div>
            <Label className="text-xs">Direzioni vento valutate</Label>
            <Input value={(aero.wind_directions || []).join(", ")} data-testid="param-aero-dirs" className="h-8 w-40"
              onChange={(e) => upd((c) => c.aero.wind_directions = e.target.value.split(",").map((s) => Number(s.trim())).filter((n) => !Number.isNaN(n)))} />
          </div>
        </div>
        {Object.entries(aero.coefficients).map(([tilt, zones]) => (
          <div key={tilt} className="mb-3 rounded-lg border border-input p-3" data-testid={`param-aero-tilt-${tilt}`}>
            <div className="mb-2 text-xs font-bold">Inclinazione PRSG {tilt}°</div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-[11px] text-muted-foreground"><tr><th className="p-1 text-left">Zona campo</th><th className="p-1">C_lift</th><th className="p-1">C_drag</th><th className="p-1">C_moment</th></tr></thead>
                <tbody>
                  {Object.entries(zones).map(([zone, cc]) => (
                    <tr key={zone} className="border-t border-input">
                      <td className="p-1 capitalize">{zone}</td>
                      {["C_lift", "C_drag", "C_moment"].map((ck) => (
                        <td key={ck} className="p-1">
                          <Input type="number" step="0.01" value={cc[ck]} data-testid={`param-aero-${tilt}-${zone}-${ck}`} className="h-7 w-20"
                            onChange={(e) => upd((c) => c.aero.coefficients[tilt][zone][ck] = Number(e.target.value))} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
        {aero.rear_sail && (
          <div className="mt-1 rounded-lg border border-input p-3" data-testid="param-aero-rear">
            <div className="mb-2 flex items-center justify-between">
              <div className="text-xs font-bold">Vela posteriore (lato alto / retro = nord · cima matrice)</div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1.5"><span className="text-[11px] text-muted-foreground">attiva</span>
                  <Switch checked={!!aero.rear_sail.enabled} onCheckedChange={(v) => upd((c) => c.aero.rear_sail.enabled = v)} data-testid="param-aero-rear-enabled" /></div>
                <div className="flex items-center gap-1.5"><span className="text-[11px] text-muted-foreground">calibrato</span>
                  <Switch checked={!!aero.rear_sail.validated} onCheckedChange={(v) => upd((c) => c.aero.rear_sail.validated = v)} data-testid="param-aero-rear-validated" /></div>
              </div>
            </div>
            <p className="mb-2 text-[11px] text-muted-foreground">Amplifica i coefficienti sui PRSG del bordo posteriore (senza pannello a nord). Convenzione: SUD in basso. Valore PROVVISORIO da calibrare con lo strutturista.</p>
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Label className="text-xs">Fattore vela</Label>
                <Input type="number" step="0.05" value={aero.rear_sail.factor} data-testid="param-aero-rear-factor" className="h-8 w-24"
                  onChange={(e) => upd((c) => c.aero.rear_sail.factor = Number(e.target.value))} />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-muted-foreground">applicato a:</span>
                {["C_lift", "C_drag", "C_moment"].map((ck) => {
                  const on = (aero.rear_sail.apply_to || []).includes(ck);
                  return (
                    <label key={ck} className="flex cursor-pointer items-center gap-1 text-[11px]">
                      <input type="checkbox" checked={on} data-testid={`param-aero-rear-apply-${ck}`}
                        onChange={(e) => upd((c) => {
                          const set = new Set(c.aero.rear_sail.apply_to || []);
                          if (e.target.checked) set.add(ck); else set.delete(ck);
                          c.aero.rear_sail.apply_to = ["C_lift", "C_drag", "C_moment"].filter((k) => set.has(k));
                        })} />
                      {ck}
                    </label>
                  );
                })}
              </div>
            </div>
          </div>
        )}
        {aero.parapet && (
          <div className="mt-2 rounded-lg border border-input p-3" data-testid="param-aero-parapet">
            <div className="mb-2 flex items-center justify-between">
              <div className="text-xs font-bold">Parapetto / protezione (riduzione su bordi/angoli)</div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1.5"><span className="text-[11px] text-muted-foreground">attivo</span>
                  <Switch checked={aero.parapet.enabled !== false} onCheckedChange={(v) => upd((c) => c.aero.parapet.enabled = v)} data-testid="param-aero-parapet-enabled" /></div>
                <div className="flex items-center gap-1.5"><span className="text-[11px] text-muted-foreground">calibrato</span>
                  <Switch checked={!!aero.parapet.validated} onCheckedChange={(v) => upd((c) => c.aero.parapet.validated = v)} data-testid="param-aero-parapet-validated" /></div>
              </div>
            </div>
            <p className="mb-2 text-[11px] text-muted-foreground">Riduzione a filo bordo f = max(f_min, 1 − k·h/Hh), con decadimento continuo in distanza g = exp(−(d/h)/λ) (d = distanza PRSG↔bordo edificio). Applicata per lato su perimetrale/angolare, angoli in caso peggiore. Valori PROVVISORI da calibrare.</p>
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2"><Label className="text-xs">k</Label>
                <Input type="number" step="0.01" value={aero.parapet.k} data-testid="param-aero-parapet-k" className="h-8 w-24"
                  onChange={(e) => upd((c) => c.aero.parapet.k = Number(e.target.value))} /></div>
              <div className="flex items-center gap-2"><Label className="text-xs">f_min</Label>
                <Input type="number" step="0.01" value={aero.parapet.f_min} data-testid="param-aero-parapet-fmin" className="h-8 w-24"
                  onChange={(e) => upd((c) => c.aero.parapet.f_min = Number(e.target.value))} /></div>
              <div className="flex items-center gap-2"><Label className="text-xs">λ (decad.)</Label>
                <Input type="number" step="0.1" value={aero.parapet.decay_lambda} data-testid="param-aero-parapet-lambda" className="h-8 w-24"
                  onChange={(e) => upd((c) => c.aero.parapet.decay_lambda = Number(e.target.value))} /></div>
              <span className="text-[11px] text-muted-foreground">zone: {(aero.parapet.zones || []).join(", ")}</span>
            </div>
          </div>
        )}
      </Card>

      {/* LIVELLO 1 - NTC */}
      <Card className="p-5" data-testid="param-ntc-card">
        <div className="mb-3 flex items-center gap-2"><Wind className="h-4 w-4 text-primary" /><h2 className="font-heading text-lg font-bold">Normativa vento NTC 2018 (versionabile)</h2></div>
        <div className="mb-3 grid grid-cols-2 gap-3 md:grid-cols-4">
          <NumField label="Densità aria ρ" suffix="kg/m³" value={ntc.rho_air} testid="param-ntc-rho" onChange={(v) => upd((c) => c.ntc.rho_air = v)} />
          <NumField label="c_t topografia default" value={ntc.ct_default} testid="param-ntc-ct" onChange={(v) => upd((c) => c.ntc.ct_default = v)} />
        </div>
        <div className="mb-3 overflow-x-auto">
          <div className="mb-1 font-mono text-[11px] font-bold uppercase tracking-widest text-primary">Zone di vento (v_b0, a_0, k_a)</div>
          <table className="w-full text-sm">
            <thead className="text-[11px] text-muted-foreground"><tr><th className="p-1 text-left">Zona</th><th className="p-1 text-left">Descrizione</th><th className="p-1">v_b0 (m/s)</th><th className="p-1">a_0 (m)</th><th className="p-1">k_a</th></tr></thead>
            <tbody>
              {Object.entries(ntc.zones).map(([z, d]) => (
                <tr key={z} className="border-t border-input">
                  <td className="p-1 font-semibold">{z}</td>
                  <td className="p-1 text-[11px] text-muted-foreground">{d.label}</td>
                  <td className="p-1"><Input type="number" step="0.5" value={d.vb0} className="h-7 w-20" data-testid={`param-zone-${z}-vb0`} onChange={(e) => upd((c) => c.ntc.zones[z].vb0 = Number(e.target.value))} /></td>
                  <td className="p-1"><Input type="number" step="50" value={d.a0} className="h-7 w-20" data-testid={`param-zone-${z}-a0`} onChange={(e) => upd((c) => c.ntc.zones[z].a0 = Number(e.target.value))} /></td>
                  <td className="p-1"><Input type="number" step="0.001" value={d.ka} className="h-7 w-24" data-testid={`param-zone-${z}-ka`} onChange={(e) => upd((c) => c.ntc.zones[z].ka = Number(e.target.value))} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="overflow-x-auto">
          <div className="mb-1 font-mono text-[11px] font-bold uppercase tracking-widest text-primary">Categorie di esposizione (k_r, z_0, z_min)</div>
          <table className="w-full text-sm">
            <thead className="text-[11px] text-muted-foreground"><tr><th className="p-1 text-left">Cat.</th><th className="p-1 text-left">Descrizione</th><th className="p-1">k_r</th><th className="p-1">z_0 (m)</th><th className="p-1">z_min (m)</th></tr></thead>
            <tbody>
              {Object.entries(ntc.terrain_categories).map(([t, d]) => (
                <tr key={t} className="border-t border-input">
                  <td className="p-1 font-semibold">{t}</td>
                  <td className="p-1 text-[11px] text-muted-foreground">{d.label}</td>
                  <td className="p-1"><Input type="number" step="0.01" value={d.kr} className="h-7 w-20" data-testid={`param-cat-${t}-kr`} onChange={(e) => upd((c) => c.ntc.terrain_categories[t].kr = Number(e.target.value))} /></td>
                  <td className="p-1"><Input type="number" step="0.01" value={d.z0} className="h-7 w-20" data-testid={`param-cat-${t}-z0`} onChange={(e) => upd((c) => c.ntc.terrain_categories[t].z0 = Number(e.target.value))} /></td>
                  <td className="p-1"><Input type="number" step="1" value={d.zmin} className="h-7 w-20" data-testid={`param-cat-${t}-zmin`} onChange={(e) => upd((c) => c.ntc.terrain_categories[t].zmin = Number(e.target.value))} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-2 text-[11px] text-muted-foreground">Località: Comune → Provincia → Zona vento (derivazione automatica). La quota s.l.m. e l'altezza edificio sono inserite nel tool finché non arrivano dal configuratore.</p>
      </Card>
    </div>
  );
}
